import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Check, Search } from 'lucide-react';
import { countries } from '../data/content';

const QUESTIONNAIRE_API = process.env.REACT_APP_QUESTIONNAIRE_API;
const COACH_EMAIL_ID = 'rasulrasheed1998@gmail.com';

// Helper function to normalize age string to number
const normalizeAge = (ageString) => {
  if (!ageString) return null;

  const str = ageString.toString().toLowerCase().trim();

  // Handle "17 and under" or similar
  if (str.includes('under') || str.includes('below')) {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 17;
  }

  // Handle "24+" or "25 and over" or similar
  if (str.includes('+') || str.includes('over') || str.includes('above')) {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 24;
  }

  // Handle ranges like "18-23" - return lower bound
  if (str.includes('-')) {
    const parts = str.split('-');
    const num = parseInt(parts[0].trim(), 10);
    return isNaN(num) ? null : num;
  }

  // Try to parse as a plain number
  const num = parseInt(str, 10);
  return isNaN(num) ? null : num;
};

const LeadQuestionnaire = () => {
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [slideDirection, setSlideDirection] = useState('next');

  // Fetch questions on mount
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${QUESTIONNAIRE_API}/GetLeadQuestionnaireTemplate`);
        if (!response.ok) throw new Error('Failed to fetch questions');
        const data = await response.json();
        const sortedQuestions = data.sort((a, b) => a.questionOrder - b.questionOrder);

        // FIX A: Filter out duplicate questions and commitment question
        const filteredQuestions = sortedQuestions.filter(q => {
          const qText = (q.text || '').toLowerCase();

          // 1) Age questions: keep ONLY questionOrder === 3
          if (qText.includes('how old are you') || qText.includes('how old')) {
            return q.questionOrder === 3;
          }

          // 2) Budget questions: keep ONLY questionOrder === 10 AND multiple-choice
          if (qText.includes('budget') || qText.includes('what does your budget look like')) {
            return q.questionOrder === 10 && (q.type || '').toLowerCase() === 'multiple-choice';
          }

          // 3) Remove commitment question entirely
          if (qText.includes('how committed can you be to reaching your physique goals')) {
            return false;
          }

          // 4) All other questions remain
          return true;
        });

        setQuestions(filteredQuestions);
        setError(null);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError('Failed to load questionnaire. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const currentQuestion = questions[currentStep];
  const totalSteps = questions.length;
  const progressValue = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const handleAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const handleOptionSelect = useCallback((questionId, optionText) => {
    handleAnswer(questionId, optionText);
    if (currentStep < totalSteps - 1) {
      setTimeout(() => {
        setSlideDirection('next');
        setCurrentStep(prev => prev + 1);
      }, 250);
    }
  }, [currentStep, totalSteps, handleAnswer]);

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setSlideDirection('next');
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, totalSteps]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setSlideDirection('prev');
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const canProceed = useCallback(() => {
    if (!currentQuestion) return false;
    const answer = answers[currentQuestion.id];
    if (currentQuestion.isRequired) {
      return answer && answer.toString().trim() !== '';
    }
    return true;
  }, [currentQuestion, answers]);

  const mapAnswersToPayload = useCallback(() => {
    const payload = {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      country: '',
      answers: {
        goal: '',
        sex: '',
        age: 0,
        motivation: '',
        sixMonthFitnessGoal: '',
        isPreparedToInvest: '',
        budgetDescription: '',
        budgetOption: '',
        reasonToWorkWithMe: '',
        commitmentLevel: '',
        timeZone: ''
      }
    };

    let lastAgeAnswer = '';

    questions.forEach((q) => {
      const answer = answers[q.id] || '';
      const qText = (q.text || '').toLowerCase();
      const orderNum = q.questionOrder;

      // FIX B: Map by questionOrder explicitly for critical fields
      if (orderNum === 1 || qText.includes('how can we help')) {
        payload.answers.goal = answer;
      } else if (orderNum === 2 || qText.includes('identify') || qText.includes('sex') || qText.includes('gender')) {
        payload.answers.sex = answer;
      } else if (orderNum === 3 || qText.includes('how old') || qText.includes('age')) {
        if (answer) lastAgeAnswer = answer;
      } else if (orderNum === 6 || qText.includes('motivation')) {
        payload.answers.motivation = answer;
      } else if (orderNum === 7 || qText.includes('6-month') || qText.includes('six month')) {
        payload.answers.sixMonthFitnessGoal = answer;
      } else if (orderNum === 8 || qText.includes('prepared to invest')) {
        payload.answers.isPreparedToInvest = answer;
      } else if (orderNum === 10 && (q.type || '').toLowerCase() === 'multiple-choice') {
        payload.answers.budgetOption = answer;
      } else if (orderNum === 11 || qText.includes('why do you want to work') || qText.includes('reason')) {
        payload.answers.reasonToWorkWithMe = answer;
      } else if (orderNum === 12 || qText.includes('commitment') || qText.includes('committed')) {
        payload.answers.commitmentLevel = answer;
      } else if (orderNum === 13 || qText.includes('time zone') || qText.includes('timezone')) {
        payload.answers.timeZone = answer;
      } else if (orderNum === 14 || qText.includes('first name')) {
        payload.firstName = answer;
      } else if (orderNum === 15 || qText.includes('last name')) {
        payload.lastName = answer;
      } else if (orderNum === 16 || qText.includes('email')) {
        payload.email = answer;
      } else if (orderNum === 17 || qText.includes('phone')) {
        payload.phoneNumber = answer;
      } else if (orderNum === 18 || qText.includes('country')) {
        // FIX B: Explicitly map country by questionOrder 18
        payload.country = answer;
      }
    });

    // Normalize age to a number (default to 0 if null/undefined)
    const normalizedAge = normalizeAge(lastAgeAnswer);
    payload.answers.age = normalizedAge !== null ? normalizedAge : 0;

    // Ensure country is trimmed
    payload.country = (payload.country || '').trim();

    return payload;
  }, [questions, answers]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const payload = mapAnswersToPayload();

      // URL-encode coachEmailId and use correct Swagger endpoint
      const encodedCoachId = encodeURIComponent(COACH_EMAIL_ID);
      const submitUrl = `${QUESTIONNAIRE_API}/SaveLeadAnswers/${encodedCoachId}`;

      console.log('Submitting to:', submitUrl);
      console.log('Payload:', JSON.stringify(payload, null, 2));
      // FIX F: Debug confirmation for country
      console.log('Country being submitted:', payload.country);

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let errorMessage = `Server error: ${response.status}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            console.error('Server error response (JSON):', errorData);
            errorMessage = errorData.message || errorData.title || errorData.error || JSON.stringify(errorData);
          } else {
            const errorText = await response.text();
            console.error('Server error response (Text):', errorText);
            if (errorText && errorText.length < 200) {
              errorMessage = errorText;
            }
          }
        } catch (parseErr) {
          console.error('Error parsing error response:', parseErr);
        }
        throw new Error(errorMessage);
      }

      console.log('Submission successful!');
      setSubmitted(true);

    } catch (err) {
      console.error('Submission error:', err);
      const userMessage = err.message && err.message.length < 100
        ? err.message
        : 'Submission failed. Please check your answers and try again.';
      setError(userMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCountries = countries.filter(c =>
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Loading State
  if (loading) {
    return (
      <div className="glass-card w-full flex flex-col">
        <div className="px-4 pt-4 pb-3 border-b border-white/10 text-center">
          <h2 className="text-lg sm:text-xl font-display uppercase tracking-widest text-white">Rasul Rasheed Coaching</h2>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-white/70 animate-spin mx-auto mb-3" />
            <p className="text-white/70 text-sm">Loading your application...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State (only for initial load failure)
  if (error && questions.length === 0) {
    return (
      <div className="glass-card w-full flex flex-col">
        <div className="px-4 pt-4 pb-3 border-b border-white/10 text-center">
          <h2 className="text-lg sm:text-xl font-display uppercase tracking-widest text-white">Rasul Rasheed Coaching</h2>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="text-center px-4">
            <p className="text-red-400 mb-4 text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (submitted) {
    return (
      <div className="glass-card w-full flex flex-col">
        <div className="px-4 pt-4 pb-3 border-b border-white/10 text-center">
          <h2 className="text-lg sm:text-xl font-display uppercase tracking-widest text-white">Rasul Rasheed Coaching</h2>
        </div>
        <div className="flex items-center justify-center py-16">
          <div className="text-center px-4">
            <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/20">
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Application Received</h3>
            <p className="text-white/60 text-sm max-w-xs mx-auto">
              If we're a good fit, you'll hear from Rasul soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isLastStep = currentStep === totalSteps - 1;
  const questionType = (currentQuestion?.type || '').toLowerCase();
  const isMultipleChoice = questionType === 'multiple-choice' || questionType === 'multiplechoice';
  const isDropdown = questionType === 'dropdown';
  const isText = questionType === 'text';
  const questionText = currentQuestion?.text || '';
  const isCountryQuestion = questionText.toLowerCase().includes('country');

  const getOptionText = (option) => {
    if (typeof option === 'object' && option !== null) {
      return option.text || '';
    }
    return option || '';
  };

  return (
    <div className="glass-card w-full flex flex-col" data-testid="lead-questionnaire">
      {/* Title Header */}
      <div className="px-4 pt-4 pb-2 text-center">
        <h2 className="text-lg sm:text-xl font-display uppercase tracking-widest text-white">Rasul Rasheed Coaching</h2>
      </div>

      {/* Progress Header */}
      <div className="px-4 pt-2 pb-3 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          {/* Back Button */}
          <button
            onClick={goBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-1 text-sm font-medium transition-all ${
              currentStep === 0
                ? 'text-white/20 cursor-not-allowed'
                : 'text-white/70 hover:text-white active:scale-95'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>

          {/* Progress Indicator */}
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-sm">{currentStep + 1}</span>
            <span className="text-white/40 text-sm">of</span>
            <span className="text-white/60 text-sm">{totalSteps}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progressValue}%` }}
          />
        </div>
      </div>

      {/* Question Area */}
      <div className="flex-1 px-4 py-5 overflow-hidden">
        <div
          className={`transition-all duration-200 ease-out ${
            slideDirection === 'next' ? 'animate-slideInRight' : 'animate-slideInLeft'
          }`}
          key={currentStep}
        >
          {/* Question Text */}
          <h3 className="text-lg sm:text-xl font-bold text-white mb-5 leading-snug">
            {questionText}
          </h3>

          {/* Multiple Choice Options */}
          {isMultipleChoice && currentQuestion?.options && !isCountryQuestion && (
            <div className="space-y-2.5">
              {currentQuestion.options.map((option, idx) => {
                const optionText = getOptionText(option);
                const isSelected = answers[currentQuestion.id] === optionText;
                return (
                  <button
                    key={idx}
                    onClick={() => handleOptionSelect(currentQuestion.id, optionText)}
                    className={`option-btn ${isSelected ? 'option-btn-selected' : ''}`}
                  >
                    <span className="font-medium">{optionText}</span>
                    {isSelected && (
                      <Check className="w-5 h-5 text-white flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Text Input */}
          {isText && (
            <div className="space-y-4">
              {questionText.toLowerCase().includes('motivation') ||
               questionText.toLowerCase().includes('goal') ||
               questionText.toLowerCase().includes('reason') ||
               questionText.toLowerCase().includes('why') ? (
                <textarea
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                  placeholder="Type your answer here..."
                  className="w-full px-4 py-4 rounded-xl border bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/50 focus:bg-white/10 transition-all outline-none min-h-[120px] resize-none caret-white"
                  style={{ color: 'white' }}
                  autoFocus
                />
              ) : (
                <input
                  type={questionText.toLowerCase().includes('email') ? 'email' :
                        questionText.toLowerCase().includes('phone') ? 'tel' : 'text'}
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                  placeholder="Type your answer..."
                  className="w-full px-4 py-4 rounded-xl border bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/50 focus:bg-white/10 transition-all outline-none min-h-[52px] caret-white"
                  style={{ color: 'white', fontSize: '16px' }}
                  autoFocus
                />
              )}

              {/* Next Button for Text Questions - NOT on last step */}
              {!isLastStep && (
                <button
                  onClick={goNext}
                  disabled={!canProceed()}
                  className={`btn-primary w-full ${!canProceed() ? 'btn-disabled' : ''}`}
                >
                  Next
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Country Dropdown */}
          {(isDropdown || isCountryQuestion) && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search your country..."
                  className="w-full pl-10 px-4 py-4 rounded-xl border bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-white/50 focus:bg-white/10 transition-all outline-none min-h-[52px] caret-white"
                  style={{ color: 'white', fontSize: '16px' }}
                  autoFocus
                />
              </div>

              {answers[currentQuestion.id] && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10 border border-white/25">
                  <Check className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">{answers[currentQuestion.id]}</span>
                </div>
              )}

              <div className="max-h-40 overflow-y-auto rounded-xl bg-black/40 border border-white/10">
                {filteredCountries.slice(0, 50).map((country) => (
                  <button
                    key={country}
                    onClick={() => {
                      handleAnswer(currentQuestion.id, country);
                      setCountrySearch('');
                    }}
                    className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-white/5 last:border-0 ${
                      answers[currentQuestion.id] === country
                        ? 'bg-white/10 text-white'
                        : 'text-white/80 hover:bg-white/5 active:bg-white/10'
                    }`}
                  >
                    {country}
                  </button>
                ))}
              </div>

              {/* Next Button for Country - NOT on last step */}
              {!isLastStep && (
                <button
                  onClick={goNext}
                  disabled={!canProceed()}
                  className={`btn-primary w-full ${!canProceed() ? 'btn-disabled' : ''}`}
                >
                  Next
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Submit Button (Last Step Only) */}
      {isLastStep && (
        <div className="px-4 pb-4 pt-2">
          {error && (
            <p className="text-red-400 text-xs text-center mb-3">{error}</p>
          )}
          <button
            onClick={handleSubmit}
            disabled={!canProceed() || submitting}
            className={`btn-primary w-full ${(!canProceed() || submitting) ? 'btn-disabled' : ''}`}
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Application'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadQuestionnaire;
